// First-run setup wizard.
//
// Shown only when the backend reports facilityStatus.configured === false.
// Step 1 — capture the facility details (name, contact, location, logo).
// Step 2 — capture the super_admin account.
// Submit posts both payloads together to /setupFacility, after which the
// user is sent to /login.
//
// This is the only place where a super_admin can be created; once one
// exists the backend refuses to re-run setup (HTTP 409).

import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  uploadLogo,
  setupFacility,
  ASSETS,
} from "../BaseURL";
import { useFacility } from "../context/FacilityContext";

export default function SetupPage() {
  const navigate = useNavigate();
  const { configured, loading: facilityLoading, refresh } = useFacility();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [facility, setFacility] = useState({
    facility_name: "",
    email: "",
    phone: "",
    location: "",
    region: "",
    digital_address: "",
    logo: "",
  });

  const [admin, setAdmin] = useState({
    admin_full_name: "",
    admin_email: "",
    admin_username: "",
    admin_phone: "",
    admin_password: "",
    admin_confirm: "",
  });

  // If the facility was already configured, bounce back to /login.
  useEffect(() => {
    if (!facilityLoading && configured) {
      navigate("/login", { replace: true });
    }
  }, [facilityLoading, configured, navigate]);

  const onFacilityChange = (e) =>
    setFacility((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onAdminChange = (e) =>
    setAdmin((a) => ({ ...a, [e.target.name]: e.target.value }));

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("logo", file);
      // Important: do NOT set Content-Type here.  axios needs to generate
      // the `multipart/form-data; boundary=…` header itself — overriding it
      // strips the boundary parameter and multer rejects the upload.
      const res = await axios.post(uploadLogo, fd);
      if (res.data?.success) {
        setFacility((f) => ({ ...f, logo: res.data.path }));
      } else {
        setError(res.data?.message || "Logo upload failed");
      }
    } catch (err) {
      console.error("Logo upload error:", err);
      setError(
        err.response?.data?.message ||
        err.message ||
        "Logo upload failed — is the backend running on port 5001?"
      );
    } finally {
      setUploadingLogo(false);
    }
  };

  const validateStep1 = () => {
    if (!facility.facility_name.trim()) return "Hotel name is required";
    return null;
  };

  const validateStep2 = () => {
    if (!admin.admin_full_name.trim()) return "Full name is required";
    if (!admin.admin_email.trim() || !admin.admin_email.includes("@"))
      return "A valid email is required";
    if (!admin.admin_username.trim()) return "Username is required";
    if (admin.admin_password.length < 6)
      return "Password must be at least 6 characters";
    if (admin.admin_password !== admin.admin_confirm)
      return "Passwords do not match";
    return null;
  };

  const goNext = () => {
    const err = validateStep1();
    if (err) { setError(err); return; }
    setError("");
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validateStep2();
    if (err) { setError(err); return; }
    setError("");
    setSubmitting(true);
    try {
      const payload = { ...facility, ...admin };
      delete payload.admin_confirm; // server doesn't need this
      const res = await axios.post(setupFacility, payload);
      if (res.data?.success) {
        // Refresh the context so Navbar / Hero / Login picks up the new
        // facility data immediately, then send the user to the login screen.
        await refresh();
        navigate("/login", { replace: true });
      } else {
        setError(res.data?.message || "Setup failed");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Setup failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (facilityLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#f5ede2", fontFamily: "'Jost', sans-serif", color: "#8a7060" }}>
        Checking system status…
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300&family=Jost:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .setup-root { min-height: 100vh; background: #f5ede2; font-family: 'Jost', sans-serif; padding: 48px 24px; }
        .setup-card { max-width: 720px; margin: 0 auto; background: #fdf8f2; border-radius: 16px; box-shadow: 0 10px 60px rgba(40,20,5,.12); overflow: hidden; border: 1px solid rgba(180,140,80,.18); }
        .setup-head { background: #2c1f14; color: #f0e4cf; padding: 36px 44px 30px; }
        .setup-eyebrow { font-size: 11px; letter-spacing: .22em; text-transform: uppercase; color: #c8a05a; margin-bottom: 10px; font-weight: 500; }
        .setup-title { font-family: 'Cormorant Garamond', serif; font-weight: 300; font-size: 34px; letter-spacing: -0.005em; }
        .setup-title em { font-style: italic; color: #c8a05a; }
        .setup-sub { color: rgba(240,228,207,.6); font-size: 13.5px; font-weight: 300; margin-top: 8px; }
        .stepper { display: flex; gap: 16px; margin-top: 26px; }
        .step-pill { display: flex; align-items: center; gap: 10px; padding: 7px 14px; border-radius: 999px; font-size: 12px; letter-spacing: .12em; text-transform: uppercase; border: 1px solid rgba(200,160,90,.3); color: rgba(240,228,207,.5); }
        .step-pill.active { background: rgba(200,160,90,.18); color: #f0e4cf; border-color: rgba(200,160,90,.6); }
        .step-num { width: 22px; height: 22px; border-radius: 50%; display: grid; place-items: center; background: rgba(200,160,90,.18); color: #c8a05a; font-size: 11px; font-weight: 600; }
        .step-pill.active .step-num { background: #c8a05a; color: #2c1f14; }
        .setup-body { padding: 36px 44px 40px; }
        .field-group { margin-bottom: 18px; }
        .field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .field-label { display: block; font-size: 10.5px; font-weight: 500; letter-spacing: .15em; text-transform: uppercase; color: #6b5a4e; margin-bottom: 7px; }
        .field-input, .field-textarea { width: 100%; padding: 12px 14px; border: 1.5px solid #e0d0c0; border-radius: 8px; background: #fff; font-family: 'Jost', sans-serif; font-size: 14px; color: #2c1f14; outline: none; transition: border-color .2s, box-shadow .2s; }
        .field-input:focus, .field-textarea:focus { border-color: #c8a05a; box-shadow: 0 0 0 3px rgba(200,160,90,.1); }
        .field-input::placeholder { color: #c0aea0; font-weight: 300; }
        .logo-row { display: flex; align-items: center; gap: 16px; padding: 14px; border: 1.5px dashed #d0bfae; border-radius: 10px; background: rgba(200,160,90,.05); }
        .logo-preview { width: 64px; height: 64px; border-radius: 50%; background: #2c1f14; display: grid; place-items: center; color: #c8a05a; overflow: hidden; flex-shrink: 0; }
        .logo-preview img { width: 100%; height: 100%; object-fit: cover; }
        .logo-pick-btn { padding: 9px 16px; background: #2c1f14; color: #f0e4cf; border: none; border-radius: 7px; cursor: pointer; font-size: 11.5px; letter-spacing: .15em; text-transform: uppercase; font-family: 'Jost', sans-serif; }
        .logo-pick-btn:disabled { opacity: .6; cursor: not-allowed; }
        .err { padding: 12px 16px; background: #fef0ee; border: 1px solid #f5c5bc; border-radius: 8px; color: #a93226; font-size: 13.5px; margin-bottom: 18px; display: flex; align-items: center; gap: 10px; }
        .actions { display: flex; gap: 12px; margin-top: 28px; justify-content: flex-end; }
        .btn-primary { padding: 13px 28px; background: #2c1f14; color: #f0e4cf; border: none; border-radius: 8px; cursor: pointer; font-family: 'Jost', sans-serif; font-size: 12px; font-weight: 500; letter-spacing: .18em; text-transform: uppercase; transition: background .2s, transform .15s; }
        .btn-primary:hover:not(:disabled) { background: #3d2a1a; transform: translateY(-1px); }
        .btn-primary:disabled { opacity: .6; cursor: not-allowed; }
        .btn-outline { padding: 13px 28px; background: transparent; color: #2c1f14; border: 1.5px solid rgba(44,31,20,.25); border-radius: 8px; cursor: pointer; font-family: 'Jost', sans-serif; font-size: 12px; font-weight: 500; letter-spacing: .18em; text-transform: uppercase; }
        .btn-outline:hover { border-color: #2c1f14; }
        .helper { font-size: 12.5px; color: #8a7060; margin-top: 6px; font-weight: 300; }
        @media (max-width: 640px) { .field-row { grid-template-columns: 1fr; } .setup-head, .setup-body { padding-left: 26px; padding-right: 26px; } }
      `}</style>

      <div className="setup-root">
        <div className="setup-card">
          <div className="setup-head">
            <div className="setup-eyebrow">Initial Setup</div>
            <div className="setup-title">
              Welcome — let's set up your <em>hotel</em>
            </div>
            <div className="setup-sub">
              You're the first user. We'll capture your hotel details and create the super admin account.
            </div>
            <div className="stepper">
              <div className={`step-pill ${step === 1 ? "active" : ""}`}>
                <span className="step-num">1</span> Facility
              </div>
              <div className={`step-pill ${step === 2 ? "active" : ""}`}>
                <span className="step-num">2</span> Super Admin
              </div>
            </div>
          </div>

          <div className="setup-body">
            {error && <div className="err">⚠️ {error}</div>}

            {step === 1 && (
              <>
                <div className="field-group">
                  <label className="field-label">Hotel Logo</label>
                  <div className="logo-row">
                    <div className="logo-preview">
                      {facility.logo
                        ? <img src={`${ASSETS}${facility.logo}`} alt="logo" />
                        : <span style={{ fontSize: 24 }}>🏨</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className="logo-pick-btn" style={{ display: "inline-block" }}>
                        {uploadingLogo ? "Uploading…" : facility.logo ? "Replace Logo" : "Upload Logo"}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={onLogoPick}
                          style={{ display: "none" }}
                          disabled={uploadingLogo}
                        />
                      </label>
                      <div className="helper">PNG / JPG up to 5MB. Square images look best.</div>
                    </div>
                  </div>
                </div>

                <div className="field-group">
                  <label className="field-label">Hotel Name *</label>
                  <input
                    className="field-input"
                    name="facility_name"
                    value={facility.facility_name}
                    onChange={onFacilityChange}
                    placeholder="e.g. Bisong Guest House"
                    required
                  />
                </div>

                <div className="field-row">
                  <div className="field-group">
                    <label className="field-label">Email</label>
                    <input
                      className="field-input"
                      name="email"
                      type="email"
                      value={facility.email}
                      onChange={onFacilityChange}
                      placeholder="info@hotel.com"
                    />
                  </div>
                  <div className="field-group">
                    <label className="field-label">Telephone</label>
                    <input
                      className="field-input"
                      name="phone"
                      value={facility.phone}
                      onChange={onFacilityChange}
                      placeholder="+233 ..."
                    />
                  </div>
                </div>

                <div className="field-row">
                  <div className="field-group">
                    <label className="field-label">Location</label>
                    <input
                      className="field-input"
                      name="location"
                      value={facility.location}
                      onChange={onFacilityChange}
                      placeholder="e.g. East Legon, Accra"
                    />
                  </div>
                  <div className="field-group">
                    <label className="field-label">Region</label>
                    <input
                      className="field-input"
                      name="region"
                      value={facility.region}
                      onChange={onFacilityChange}
                      placeholder="e.g. Greater Accra"
                    />
                  </div>
                </div>

                <div className="field-group">
                  <label className="field-label">Digital Address</label>
                  <input
                    className="field-input"
                    name="digital_address"
                    value={facility.digital_address}
                    onChange={onFacilityChange}
                    placeholder="e.g. GA-123-4567"
                  />
                </div>

                <div className="actions">
                  <button type="button" className="btn-primary" onClick={goNext}>
                    Continue →
                  </button>
                </div>
              </>
            )}

            {step === 2 && (
              <form onSubmit={submit}>
                <div className="field-group">
                  <label className="field-label">Full Name *</label>
                  <input
                    className="field-input"
                    name="admin_full_name"
                    value={admin.admin_full_name}
                    onChange={onAdminChange}
                    placeholder="Your full name"
                    required
                  />
                </div>

                <div className="field-row">
                  <div className="field-group">
                    <label className="field-label">Email *</label>
                    <input
                      className="field-input"
                      type="email"
                      name="admin_email"
                      value={admin.admin_email}
                      onChange={onAdminChange}
                      placeholder="you@hotel.com"
                      required
                    />
                  </div>
                  <div className="field-group">
                    <label className="field-label">Username *</label>
                    <input
                      className="field-input"
                      name="admin_username"
                      value={admin.admin_username}
                      onChange={onAdminChange}
                      placeholder="e.g. admin"
                      required
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="field-group">
                  <label className="field-label">Telephone</label>
                  <input
                    className="field-input"
                    name="admin_phone"
                    value={admin.admin_phone}
                    onChange={onAdminChange}
                    placeholder="+233 ..."
                  />
                </div>

                <div className="field-row">
                  <div className="field-group">
                    <label className="field-label">Password *</label>
                    <input
                      className="field-input"
                      type="password"
                      name="admin_password"
                      value={admin.admin_password}
                      onChange={onAdminChange}
                      placeholder="Min. 6 characters"
                      required
                    />
                  </div>
                  <div className="field-group">
                    <label className="field-label">Confirm Password *</label>
                    <input
                      className="field-input"
                      type="password"
                      name="admin_confirm"
                      value={admin.admin_confirm}
                      onChange={onAdminChange}
                      placeholder="Re-type password"
                      required
                    />
                  </div>
                </div>

                <div className="helper">
                  This account will be the only one that can create other users.
                  You'll log in with the username (not the email).
                </div>

                <div className="actions">
                  <button type="button" className="btn-outline" onClick={() => { setError(""); setStep(1); }}>
                    ← Back
                  </button>
                  <button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? "Setting up…" : "Complete Setup"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
